import { useCallback, useState } from 'react'
import { Alert, Button, Drawer, Empty, Flex, Grid, Skeleton, Typography } from 'antd'
import { FileExcelOutlined, PrinterOutlined } from '@ant-design/icons'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue } from '@/shared/lib/money'
import { LoadError } from '@/shared/ui/LoadError'
import {
  downloadHistoryExcel,
  fetchHistoryPage,
  fetchStatement,
  isOutOfScope,
  type Statement,
} from './historyApi'
import { historyKeys } from './historyKeys'
import { presetPeriod, type HistoryPeriod } from './historyPeriod'
import { HistoryPeriodPicker } from './HistoryPeriodPicker'
import { HistoryRows } from './HistoryRows'
import { StatementPrint } from './StatementPrint'

export interface HistorySubscriber {
  id: string
  login: string
}

interface SubscriberHistoryDrawerProps {
  subscriber: HistorySubscriber | null
  onClose: () => void
}

export function SubscriberHistoryDrawer({ subscriber, onClose }: SubscriberHistoryDrawerProps) {
  const { t } = useTranslation()
  const isMobile = Grid.useBreakpoint().md === false

  return (
    <Drawer
      open={subscriber !== null}
      onClose={onClose}
      placement={isMobile ? 'bottom' : 'right'}
      {...(isMobile ? { height: '100%' } : { width: 'min(1040px, 100vw)' })}
      title={subscriber ? t('history.titleFor', { login: subscriber.login }) : t('history.title')}
      destroyOnHidden
    >
      {subscriber ? <HistoryBody subscriber={subscriber} /> : null}
    </Drawer>
  )
}

function HistoryBody({ subscriber }: { subscriber: HistorySubscriber }) {
  const { t } = useTranslation()
  const [period, setPeriod] = useState<HistoryPeriod>(() => presetPeriod('days90'))
  const [statement, setStatement] = useState<Statement | null>(null)

  const history = useInfiniteQuery({
    queryKey: historyKeys.page(subscriber.id, period),
    queryFn: ({ pageParam, signal }) => fetchHistoryPage(subscriber.id, period, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasNext ? last.page + 1 : undefined),
  })

  const excel = useMutation({
    mutationFn: () => downloadHistoryExcel(subscriber.id, subscriber.login, period),
  })

  const print = useMutation({
    mutationFn: () => fetchStatement(subscriber.id, period),
    onSuccess: setStatement,
  })

  const changePeriod = (next: HistoryPeriod) => {
    excel.reset()
    print.reset()
    setPeriod(next)
  }

  const finishPrint = useCallback(() => setStatement(null), [])

  if (history.isError && isOutOfScope(history.error)) {
    return <Alert type="info" showIcon message={t('history.notYours')} />
  }

  const first = history.data?.pages[0]
  const rows = history.data?.pages.flatMap((page) => page.rows) ?? []

  return (
    <Flex vertical gap={16}>
      <HistoryPeriodPicker period={period} onChange={changePeriod} />

      <Flex gap={8} wrap>
        <Button
          icon={<FileExcelOutlined />}
          loading={excel.isPending}
          disabled={!history.isSuccess}
          onClick={() => excel.mutate()}
        >
          {t('history.excel')}
        </Button>
        <Button
          icon={<PrinterOutlined />}
          loading={print.isPending}
          disabled={!history.isSuccess}
          onClick={() => print.mutate()}
        >
          {t('history.print')}
        </Button>
      </Flex>

      {excel.isError ? <LoadError titleKey="history.excelFailed" error={excel.error} /> : null}
      {print.isError ? <LoadError titleKey="history.printFailed" error={print.error} /> : null}

      {history.isError ? <LoadError titleKey="history.loadFailed" error={history.error} /> : null}
      {history.isPending ? <Skeleton active paragraph={{ rows: 6 }} /> : null}

      {first ? (
        <>
          <Flex gap={24} wrap>
            <Typography.Text>
              {t('history.openingBalance')}: <strong>{formatMoneyValue(first.opening)}</strong>
            </Typography.Text>
            <Typography.Text>
              {t('history.closingBalance')}: <strong>{formatMoneyValue(first.closing)}</strong>
            </Typography.Text>
          </Flex>

          {rows.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('history.empty')} />
          ) : (
            <HistoryRows rows={rows} />
          )}

          {history.hasNextPage ? (
            <Button block loading={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
              {t('history.loadMore')}
            </Button>
          ) : null}
        </>
      ) : null}

      {statement ? <StatementPrint key={statement.generatedAt} statement={statement} onDone={finishPrint} /> : null}
    </Flex>
  )
}
